import React, { useState, useEffect } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import axios from "axios";
import PatientInfoForm from "../Forms/PatientInfoForm";
import CONFIG from '../../config';
import { useAuth } from "../../context/AuthProvider";
import { usePatient } from "../../context/PatientContext";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";

const AddPatientInfo = () => {
    const { markSectionComplete } = useOutletContext();
    const { theme } = useTheme();
    const baseURL = CONFIG.BASE_URL;
    const navigate = useNavigate();
    const { verifyTokenAndRedirect } = useAuth();
    const { token, doctorInfo, patientData, setPatientData } = usePatient();
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        verifyTokenAndRedirect();
        // Initialize patient data structure if it's not a new case session
        if (!patientData || patientData.ACTION_STATUS !== "ADD_NEW_CASE") {
            setPatientData({
                ACTION_STATUS: "ADD_NEW_CASE",
                patientInfo: [{}],
                patientSymptoms: [{}],
                patientHistory: [{}],
                patientClinicalInvestigation: [{}],
                patientAnyavyadhi: [{}],
                patientKulawrutta: [{}],
                patientTapasanya: [{}],
                patientArtawawrutta: [{}],
                patientDiagnosis: [{}],
                patientTreatment: [{}],
                patientNextVisit: [{}],
                patientPayment: [{}]
            });
        }
    }, [verifyTokenAndRedirect, setPatientData, patientData]);

    const handleFormSubmit = async (values) => {
        setIsLoading(true);
        try {
            const patientInfoData = {
                ...values,
                DOCTOR_ID: doctorInfo.MANDALI_ID,
                CLINIC_ID: doctorInfo.CLINIC_ID
            };

            console.log('Creating new patient:', patientInfoData);

            const response = await axios.post(`${baseURL}/patients/addpatient`, patientInfoData, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    doctorId: doctorInfo.MANDALI_ID,
                    clinicId: doctorInfo.CLINIC_ID,
                    roleId: doctorInfo.MANDALI_ROLE
                }
            });

            if (response.data && response.data[1]?.newPatientInfo?.PATIENT_ID) {
                const newPatientId = response.data[1].newPatientInfo.PATIENT_ID;
                console.log('Patient created with ID:', newPatientId);

                // Update context with the new PATIENT_ID and the info we just submitted
                const updatedData = {
                    ...patientData,
                    PATIENT_ID: newPatientId,
                    ACTION_STATUS: "ADD_NEW_CASE",
                    patientInfo: [{
                        ...values,
                        PATIENT_ID: newPatientId,
                        DOCTOR_ID: doctorInfo.MANDALI_ID,
                        CLINIC_ID: doctorInfo.CLINIC_ID
                    }]
                };

                setPatientData(updatedData);
                markSectionComplete("info");
                setIsLoading(false);

                // Navigate to the next step in the sidebar
                navigate('/addpatient/symptoms');
            } else {
                throw new Error('Failed to get patient ID from response');
            }
        } catch (error) {
            console.error('Error creating patient:', error);
            alert('Error creating patient. Please try again.');
            setIsLoading(false);
        }
    };

    const handleAutoSave = (values) => {
        const patientInfoData = {
            ...values,
            DOCTOR_ID: doctorInfo.MANDALI_ID,
            CLINIC_ID: doctorInfo.CLINIC_ID
        };

        setPatientData((prev) => ({
            ...(prev || {}),
            ACTION_STATUS: "ADD_NEW_CASE",
            patientInfo: [{ ...patientInfoData }]
        }));
    };

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center h-full">
                <Loader2 className="animate-spin mb-4" size={40} style={{ color: theme.primary }} />
                <p className="font-medium opacity-60" style={{ color: theme.text }}>Generating medical record...</p>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="mb-10">
                <h3
                    className="text-2xl md:text-4xl mb-3 tracking-tight"
                    style={{
                        fontFamily: theme.fonts?.serif,
                        color: theme.text
                    }}
                >
                    New Patient Registration
                </h3>
                <div className="h-1 w-12 rounded-full mb-6" style={{ backgroundColor: theme.primary }} />
                <p className="text-sm md:text-base opacity-60 font-medium" style={{ color: theme.textSecondary }}>
                    Initialize a new medical file by entering the patient's primary contact and demographic details.
                </p>
            </div>

            <PatientInfoForm
                initialValues={patientData}
                onSubmit={handleFormSubmit}
                onAutoSave={handleAutoSave}
                isFirstStep={true}
                isLastStep={false}
            />
        </div>
    );
};

export default AddPatientInfo;
